import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_MODULE_MESSAGES, UserAccountService } from '../services/user-account.service';
import { environment } from '../../environments/environment';
import { AppComponent } from '../app.component';
import { ConfigurationService } from '../services/configuration.service';
import { TestLoginService } from '../services/test-login.service';
import { TestUserCatalogService } from '../services/test-user-catalog.service';
import { TestLoginDialogComponent } from '../test-login-dialog/test-login-dialog.component';

type SafeRedirectTarget =
  | { kind: 'internal'; route: string }
  | { kind: 'external'; url: URL };

@Component({
  selector: 'app-noaccess',
  templateUrl: './noaccess.component.html',
  styleUrls: ['./noaccess.component.scss'],
  standalone: false,
})
export class NoAccessComponent implements OnInit, OnDestroy {
  public contactMessage: SafeHtml;
  public environment = environment;
  public userMessage: string = '';
  public returnUrl: string = '/';
  public isCheckingPermissions = false;
  public isRedirecting = false;
  public isFallbackRedirect = false;
  public countdown = 3;
  private readonly noAccessMessageDisplayMs = 3000;
  private redirectDelayTimeout: ReturnType<typeof setTimeout> | null = null;
  private redirectInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private configService: ConfigurationService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private testUserCatalog: TestUserCatalogService,
    private testLoginService: TestLoginService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserAccountService,
    private appComponent: AppComponent,
  ) {
    const contactMessage = this.configService.getConfig().noAccessContactMessage;
    this.contactMessage = this.sanitizer.bypassSecurityTrustHtml(contactMessage); // Prevents XSS
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const module = params['module'];
      this.returnUrl = params['returnUrl'];

      if (module && APP_MODULE_MESSAGES[module]) {
        this.userMessage = APP_MODULE_MESSAGES[module];
      } else {
        this.userMessage = ''; // Default handled in template
      }

      // Always check permissions to handle direct access or returnUrl scenarios
      this.checkPermissions();
    });
  }

  ngOnDestroy(): void {
    this.clearRedirectTimers();
  }

  async checkPermissions() {
    this.isCheckingPermissions = true;
    try {
      await this.userService.refreshUserRoles();
      // Check if we actually have valid roles now
      if (this.userService.hasValidRole()) {
        let target = this.returnUrl;
        let fallbackRedirect = false;

        // If no specific return URL, or it's potentially ambiguous, try to infer home
        if (!target || target === '/' || target.includes('noaccess')) {
          if (this.userService.isLPUser()) {
            target = '/experiment-list';
          } else if (this.userService.isICUser()) {
            target = '/image-capture/experiments';
          } else if (this.userService.isClinicalTrial()) {
            target = '/bundles';
          } else if (this.userService.isAdmin()) {
            target = '/admin/assays';
          } else if (this.userService.isCAUser()) {
            target = this.findSafeHome();
          }
        } else {
          // If we have a target but still don't have access to it?
          // We can't easily check router config again here without duplicating guard logic.
          // But if we just got roles, let's assume if we are not the specific role for that target,
          // we should fallback to a safe home.
          // Simple heuristic: if target contains 'image-capture' but we aren't IC user...
          if (target.includes('image-capture') && !this.userService.isICUser()) {
            target = this.findSafeHome();
            fallbackRedirect = true;
          } else if (target.includes('bundles') && !this.userService.isClinicalTrial()) {
            target = this.findSafeHome();
            fallbackRedirect = true;
          } else if (target.includes('savanna-results') && !this.userService.isLPUser()) {
            target = this.findSafeHome();
            fallbackRedirect = true;
          } else if (target.includes('admin') && !this.userService.isAdmin()) {
            target = this.findSafeHome();
            fallbackRedirect = true;
          }
          // Add more heuristics as needed or just rely on the router to bounce them back if they fail again
        }

        const safeRedirectTarget = target ? this.resolveSafeRedirectTarget(target) : null;

        // Only redirect if we have a valid target that isn't ourselves.
        if (safeRedirectTarget && !this.isNoAccessTarget(safeRedirectTarget)) {
          this.startRedirectFlow(safeRedirectTarget, fallbackRedirect);
        }
      }
    } catch (error) {
      console.error('Error checking permissions', error);
    } finally {
      this.isCheckingPermissions = false;
    }
  }

  private startRedirectFlow(target: SafeRedirectTarget, fallbackRedirect: boolean): void {
    this.clearRedirectTimers();

    // Keep the no-access message visible briefly before starting countdown.
    this.isRedirecting = false;
    this.isFallbackRedirect = fallbackRedirect;
    this.countdown = 3;

    this.redirectDelayTimeout = setTimeout(() => {
      this.isRedirecting = true;

      this.redirectInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.clearRedirectTimers();
          if (target.kind === 'external') {
            this.navigateToValidatedExternalTarget(target.url);
            return;
          }

          this.router.navigateByUrl(target.route);
        }
      }, 1000);
    }, this.noAccessMessageDisplayMs);
  }

  private resolveSafeRedirectTarget(target: string): SafeRedirectTarget | null {
    const trimmedTarget = target.trim();
    if (!trimmedTarget) {
      return null;
    }

    if (trimmedTarget.startsWith('/')) {
      if (trimmedTarget.startsWith('//')) {
        return null;
      }

      return {
        kind: 'internal',
        route: trimmedTarget,
      };
    }

    const externalTarget = this.resolveAllowedExternalTarget(trimmedTarget);
    if (!externalTarget) {
      return null;
    }

    return {
      kind: 'external',
      url: externalTarget,
    };
  }

  private resolveAllowedExternalTarget(target: string): URL | null {
    const targetUrl = this.tryCreateUrl(target);
    const configuredBaseUrl = this.getConfiguredExternalBaseUrl(
      this.configService.getConfig().cloudAnalysisUiBaseUrl,
    );

    if (!targetUrl || !configuredBaseUrl) {
      return null;
    }

    if (targetUrl.origin !== configuredBaseUrl.origin) {
      return null;
    }

    const normalizedTargetPath = this.normalizePath(targetUrl.pathname);
    const normalizedBasePath = this.normalizePath(configuredBaseUrl.pathname);

    if (normalizedBasePath !== '/' && normalizedTargetPath !== normalizedBasePath && !normalizedTargetPath.startsWith(`${normalizedBasePath}/`)) {
      return null;
    }

    return targetUrl;
  }

  private isNoAccessTarget(target: SafeRedirectTarget): boolean {
    if (target.kind === 'internal') {
      return target.route.includes('noaccess');
    }

    return this.normalizePath(target.url.pathname).includes('/noaccess');
  }

  private navigateToValidatedExternalTarget(targetUrl: URL): void {
    window.location.assign(targetUrl.toString());
  }

  private getConfiguredExternalBaseUrl(baseUrl?: string): URL | null {
    if (!baseUrl) {
      return null;
    }

    const parsedBaseUrl = this.tryCreateUrl(baseUrl.trim());
    if (!parsedBaseUrl) {
      return null;
    }

    parsedBaseUrl.pathname = this.normalizePath(parsedBaseUrl.pathname);
    return parsedBaseUrl;
  }

  private tryCreateUrl(value: string): URL | null {
    try {
      return new URL(value);
    } catch {
      return null;
    }
  }

  private normalizePath(pathname: string): string {
    const normalizedPath = pathname.replace(/\/+$/, '');
    return normalizedPath || '/';
  }

  private clearRedirectTimers(): void {
    if (this.redirectDelayTimeout) {
      clearTimeout(this.redirectDelayTimeout);
      this.redirectDelayTimeout = null;
    }

    if (this.redirectInterval) {
      clearInterval(this.redirectInterval);
      this.redirectInterval = null;
    }
  }

  findSafeHome(): string {
    if (this.userService.isLPUser()) return '/experiment-list';
    if (this.userService.isICUser()) return '/image-capture/experiments';
    if (this.userService.isClinicalTrial()) return '/bundles';
    if (this.userService.isCAUser()) return this.configService.getConfig().cloudAnalysisUiBaseUrl || '';
    return '/';
  }

  showTestLoginDialog(): void {
    const dialogRef = this.dialog.open(TestLoginDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        testUsers: this.testUserCatalog.getTestUsers(),
        currentUser: this.testUserCatalog.getCurrentUser(),
      },
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result && result.success && result.user) {
        try {
          // Login with test token
          await this.testLoginService.loginWithTestToken(result.user);

          // Set the selected user in the catalog
          this.testUserCatalog.setCurrentUser(result.user);

          // Use AppComponent's method to set the user in the account service
          this.appComponent.setTestUserInAccountService(result.user.id);

          // Update AppComponent's display state
          this.appComponent.loginDisplay = true;
          this.appComponent.displayName = result.user.name;

          // Navigate to home page
          this.router.navigate(['/']);
        } catch (error) {
          console.error('[NoAccess] Error logging in test user:', error);
        }
      }
    });
  }
}
